import { Page, View, TabView } from 'web-mojo';

// ── Helpers ──────────────────────────────────────────────────────────────────

function isoDate(d) {
    return d.toISOString().slice(0, 10);
}

function defaultDateRange() {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 29);
    return { from: isoDate(from), to: isoDate(to) };
}

function exportCsv(filename, rows) {
    if (!rows || rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const lines = [
        headers.join(','),
        ...rows.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// ── Base analytics tab ────────────────────────────────────────────────────────

class AnalyticsTab extends View {
    constructor() {
        super();
        const { from, to } = defaultDateRange();
        this.dateFrom = from;
        this.dateTo = to;
        this.data = null;
        this.loading = false;
        this.error = null;
        this._chart = null;
    }

    async onInit() {
        await this.fetchData();
    }

    // Subclasses implement these
    async fetchData() {}
    getExportFilename() { return 'analytics.csv'; }
    getExportRows() { return this.data || []; }

    async onActionApplyFilter() {
        const fromEl = this.element.querySelector('[name=date_from]');
        const toEl = this.element.querySelector('[name=date_to]');
        if (fromEl) this.dateFrom = fromEl.value;
        if (toEl) this.dateTo = toEl.value;
        await this.fetchData();
    }

    onActionExport() {
        exportCsv(this.getExportFilename(), this.getExportRows());
    }

    _filterBarHtml(extraFilters = '') {
        return `
            <div class="d-flex flex-wrap gap-2 align-items-end mb-3">
                <div>
                    <label class="form-label small mb-1">From</label>
                    <input type="date" class="form-control form-control-sm" name="date_from" value="${this.dateFrom}" style="width:140px;">
                </div>
                <div>
                    <label class="form-label small mb-1">To</label>
                    <input type="date" class="form-control form-control-sm" name="date_to" value="${this.dateTo}" style="width:140px;">
                </div>
                ${extraFilters}
                <button type="button" class="btn btn-sm btn-primary" data-action="apply-filter">Apply</button>
                <button type="button" class="btn btn-sm btn-outline-secondary ms-auto" data-action="export">
                    <i class="bi bi-download me-1"></i>Export CSV
                </button>
            </div>
        `;
    }

    _loadingHtml() {
        return `<div class="text-center py-5 text-muted"><div class="spinner-border spinner-border-sm me-2"></div>Loading…</div>`;
    }

    _errorHtml() {
        return `<div class="alert alert-danger small">${this.error}</div>`;
    }

    _emptyHtml() {
        return `<div class="text-center py-4 text-muted small">No data for this period.</div>`;
    }
}

// ── Completion Rate Tab ───────────────────────────────────────────────────────

class CompletionRateTab extends AnalyticsTab {
    constructor() {
        super();
        this.operators = [];
        this.operatorFilter = '';
    }

    async onInit() {
        const opResponse = await app.api.get('/api/routes/operator', {
            group: app.groupId(),
            status: 'active',
            size: 200,
        });
        if (opResponse.success && opResponse.data.status) {
            this.operators = opResponse.data.data || [];
        }
        await this.fetchData();
    }

    async onActionApplyFilter() {
        const fromEl = this.element.querySelector('[name=date_from]');
        const toEl = this.element.querySelector('[name=date_to]');
        const opEl = this.element.querySelector('[name=operator_filter]');
        if (fromEl) this.dateFrom = fromEl.value;
        if (toEl) this.dateTo = toEl.value;
        if (opEl) this.operatorFilter = opEl.value;
        await this.fetchData();
    }

    async fetchData() {
        this.loading = true;
        this.error = null;
        this.render();

        const params = { date_from: this.dateFrom, date_to: this.dateTo };
        if (this.operatorFilter) params.operator = this.operatorFilter;

        const response = await app.api.get('/api/routes/analytics/completion', params);
        this.loading = false;
        if (!response.success || !response.data.status) {
            this.error = response.data?.error || 'Failed to load completion data';
            this.data = null;
        } else {
            this.data = response.data.data || [];
        }
        this.render();
        this._mountChart();
    }

    _mountChart() {
        if (!this.data || this.data.length === 0) return;
        const container = this.element?.querySelector('[data-container="chart"]');
        if (!container) return;

        import('web-mojo/charts').then(({ SeriesChart }) => {
            if (this._chart) {
                this._chart.setData(this.data);
                return;
            }
            this._chart = new SeriesChart({
                title: 'Route Completion Rate',
                chartType: 'bar',
                xAxis: { field: 'date', label: 'Date' },
                yAxis: { field: 'completion_rate', label: 'Completion %', beginAtZero: true },
                tooltip: { x: 'date', y: 'number:0' },
                colors: ['rgba(25, 135, 84, 0.75)'],
            });
            this._chart.setData(this.data);
            this.addChild(this._chart, { containerId: 'chart' });
        }).catch(() => {
            // Chart unavailable — table-only mode
        });
    }

    getExportFilename() { return `completion_${this.dateFrom}_${this.dateTo}.csv`; }

    _tableHtml() {
        if (!this.data || this.data.length === 0) return '';
        const rows = this.data.map(row => `
            <tr>
                <td class="small">${row.date || row.scheduled_date || '—'}</td>
                <td class="small">${row.total ?? '—'}</td>
                <td class="small">${row.completed ?? '—'}</td>
                <td class="small">
                    <div class="d-flex align-items-center gap-2">
                        <div class="progress flex-grow-1" style="height:6px;">
                            <div class="progress-bar bg-success" style="width:${row.completion_rate ?? 0}%"></div>
                        </div>
                        <span>${row.completion_rate != null ? `${row.completion_rate}%` : '—'}</span>
                    </div>
                </td>
            </tr>
        `).join('');

        return `
            <div class="table-responsive mt-3">
                <table class="table table-sm table-hover">
                    <thead class="table-light">
                        <tr>
                            <th>Date</th><th>Total Routes</th><th>Completed</th><th>Completion Rate</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
    }

    get template() {
        const opOptions = this.operators.map(op => {
            const sel = String(op.id) === this.operatorFilter ? 'selected' : '';
            return `<option value="${op.id}" ${sel}>${op.display_name || op.id}</option>`;
        }).join('');

        const extraFilters = `
            <div>
                <label class="form-label small mb-1">Operator</label>
                <select class="form-select form-select-sm" name="operator_filter" style="width:160px;">
                    <option value="">All Operators</option>
                    ${opOptions}
                </select>
            </div>
        `;

        return `
            <div class="p-3">
                ${this._filterBarHtml(extraFilters)}
                ${this.loading ? this._loadingHtml() : ''}
                ${this.error ? this._errorHtml() : ''}
                ${!this.loading && !this.error && this.data?.length === 0 ? this._emptyHtml() : ''}
                ${!this.loading && !this.error && this.data?.length > 0 ? `
                    <div data-container="chart" style="height:260px;"></div>
                    ${this._tableHtml()}
                ` : ''}
            </div>
        `;
    }
}

// ── Dwell Time Tab ────────────────────────────────────────────────────────────

class DwellTimeTab extends AnalyticsTab {
    async fetchData() {
        this.loading = true;
        this.error = null;
        this.render();

        const response = await app.api.get('/api/routes/analytics/dwell', {
            date_from: this.dateFrom,
            date_to: this.dateTo,
        });
        this.loading = false;
        if (!response.success || !response.data.status) {
            this.error = response.data?.error || 'Failed to load dwell data';
            this.data = null;
        } else {
            this.data = response.data.data || [];
        }
        this.render();
        this._mountChart();
    }

    _mountChart() {
        if (!this.data || this.data.length === 0) return;
        const container = this.element?.querySelector('[data-container="chart"]');
        if (!container) return;

        import('web-mojo/charts').then(({ SeriesChart }) => {
            if (this._chart) {
                this._chart.setData(this.data);
                return;
            }
            this._chart = new SeriesChart({
                title: 'Average Dwell Time by Activity',
                chartType: 'bar',
                orientation: 'horizontal',
                xAxis: { field: 'avg_dwell_minutes', label: 'Avg Minutes', beginAtZero: true },
                yAxis: { field: 'activity_type', label: 'Activity' },
                colors: ['rgba(13, 110, 253, 0.75)'],
            });
            this._chart.setData(this.data);
            this.addChild(this._chart, { containerId: 'chart' });
        }).catch(() => {});
    }

    getExportFilename() { return `dwell_time_${this.dateFrom}_${this.dateTo}.csv`; }

    _tableHtml() {
        if (!this.data || this.data.length === 0) return '';
        const rows = this.data.map(row => `
            <tr>
                <td class="small text-capitalize">${row.activity_type || '—'}</td>
                <td class="small">${row.avg_dwell_minutes != null ? `${row.avg_dwell_minutes} min` : '—'}</td>
                <td class="small">${row.total_visits ?? '—'}</td>
                <td class="small">${row.min_dwell_minutes != null ? `${row.min_dwell_minutes} min` : '—'}</td>
                <td class="small">${row.max_dwell_minutes != null ? `${row.max_dwell_minutes} min` : '—'}</td>
            </tr>
        `).join('');

        return `
            <div class="table-responsive mt-3">
                <table class="table table-sm table-hover">
                    <thead class="table-light">
                        <tr>
                            <th>Activity</th><th>Avg Dwell</th><th>Visits</th><th>Min</th><th>Max</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
    }

    get template() {
        return `
            <div class="p-3">
                ${this._filterBarHtml()}
                ${this.loading ? this._loadingHtml() : ''}
                ${this.error ? this._errorHtml() : ''}
                ${!this.loading && !this.error && this.data?.length === 0 ? this._emptyHtml() : ''}
                ${!this.loading && !this.error && this.data?.length > 0 ? `
                    <div data-container="chart" style="height:260px;"></div>
                    ${this._tableHtml()}
                ` : ''}
            </div>
        `;
    }
}

// ── Operator Performance Tab ──────────────────────────────────────────────────

class OperatorPerformanceTab extends AnalyticsTab {
    async fetchData() {
        this.loading = true;
        this.error = null;
        this.render();

        const response = await app.api.get('/api/routes/analytics/operator_performance', {
            date_from: this.dateFrom,
            date_to: this.dateTo,
        });
        this.loading = false;
        if (!response.success || !response.data.status) {
            this.error = response.data?.error || 'Failed to load operator performance data';
            this.data = null;
        } else {
            this.data = response.data.data || [];
        }
        this.render();
        this._mountChart();
    }

    _mountChart() {
        if (!this.data || this.data.length === 0) return;

        import('web-mojo/charts').then(({ SeriesChart }) => {
            if (this._chart) {
                this._chart.setData(this.data);
                return;
            }
            this._chart = new SeriesChart({
                title: 'Completion Rate by Operator',
                chartType: 'bar',
                orientation: 'horizontal',
                xAxis: { field: 'avg_completion_rate', label: 'Avg Completion %', beginAtZero: true },
                yAxis: { field: 'operator_name', label: 'Operator' },
                colors: ['rgba(25, 135, 84, 0.75)'],
            });
            this._chart.setData(this.data);
            this.addChild(this._chart, { containerId: 'chart' });
        }).catch(() => {});
    }

    getExportFilename() { return `operator_performance_${this.dateFrom}_${this.dateTo}.csv`; }

    _tableHtml() {
        if (!this.data || this.data.length === 0) return '';
        const rows = this.data.map(row => `
            <tr>
                <td class="small fw-semibold">${row.operator_name || row.operator_id || '—'}</td>
                <td class="small">${row.routes_completed ?? '—'}</td>
                <td class="small">${row.stops_completed ?? '—'}</td>
                <td class="small">
                    ${row.avg_completion_rate != null ? `
                        <div class="d-flex align-items-center gap-2">
                            <div class="progress flex-grow-1" style="height:6px;">
                                <div class="progress-bar bg-success" style="width:${row.avg_completion_rate}%"></div>
                            </div>
                            <span>${row.avg_completion_rate}%</span>
                        </div>
                    ` : '—'}
                </td>
                <td class="small">${row.avg_dwell_minutes != null ? `${row.avg_dwell_minutes} min` : '—'}</td>
                <td class="small">${row.skipped_stops ?? '—'}</td>
            </tr>
        `).join('');

        return `
            <div class="table-responsive mt-3">
                <table class="table table-sm table-hover">
                    <thead class="table-light">
                        <tr>
                            <th>Operator</th>
                            <th>Routes</th>
                            <th>Stops</th>
                            <th>Completion Rate</th>
                            <th>Avg Dwell</th>
                            <th>Skipped</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
    }

    get template() {
        return `
            <div class="p-3">
                ${this._filterBarHtml()}
                ${this.loading ? this._loadingHtml() : ''}
                ${this.error ? this._errorHtml() : ''}
                ${!this.loading && !this.error && this.data?.length === 0 ? this._emptyHtml() : ''}
                ${!this.loading && !this.error && this.data?.length > 0 ? `
                    <div data-container="chart" style="height:260px;"></div>
                    ${this._tableHtml()}
                ` : ''}
            </div>
        `;
    }
}

// ── Missed Stops Tab (partial — skipped stops) ────────────────────────────────

class MissedStopsTab extends AnalyticsTab {
    async fetchData() {
        this.loading = true;
        this.error = null;
        this.render();

        const response = await app.api.get('/api/routes/stop', {
            stop__route__group: app.groupId(),
            status: 'skipped',
            scheduled_date__gte: this.dateFrom,
            scheduled_date__lte: this.dateTo,
            size: 200,
        });
        this.loading = false;
        if (!response.success || !response.data.status) {
            this.error = response.data?.error || 'Failed to load skipped stop data';
            this.data = null;
        } else {
            this.data = response.data.data || [];
        }
        this.render();
    }

    getExportFilename() { return `missed_stops_${this.dateFrom}_${this.dateTo}.csv`; }
    getExportRows() {
        return (this.data || []).map(s => ({
            date: s.route?.scheduled_date || '',
            location: s.location?.name || s.label || '',
            operator: s.operator?.display_name || '',
            reason: s.skip_reason || '',
            reason_code: s.reason_code || '',
        }));
    }

    _tableHtml() {
        if (!this.data || this.data.length === 0) return '';
        const rows = this.data.map(s => `
            <tr>
                <td class="small">${s.route?.scheduled_date || '—'}</td>
                <td class="small">${s.location?.name || s.label || '—'}</td>
                <td class="small">${s.route?.template?.name || '—'}</td>
                <td class="small">${s.operator?.display_name || s.route?.operator?.display_name || '—'}</td>
                <td class="small">${s.skip_reason || s.reason || '—'}</td>
            </tr>
        `).join('');

        return `
            <div class="text-muted small mb-2">
                <i class="bi bi-info-circle me-1"></i>
                Showing individual skipped stops. A dedicated skip-rate analytics endpoint is not yet available.
            </div>
            <div class="table-responsive">
                <table class="table table-sm table-hover">
                    <thead class="table-light">
                        <tr>
                            <th>Date</th><th>Location</th><th>Route</th><th>Operator</th><th>Reason</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
    }

    get template() {
        return `
            <div class="p-3">
                ${this._filterBarHtml()}
                ${this.loading ? this._loadingHtml() : ''}
                ${this.error ? this._errorHtml() : ''}
                ${!this.loading && !this.error && this.data?.length === 0 ? this._emptyHtml() : ''}
                ${!this.loading && !this.error && this.data?.length > 0 ? this._tableHtml() : ''}
            </div>
        `;
    }
}

// ── Placeholder tab ───────────────────────────────────────────────────────────

class PlaceholderTab extends View {
    constructor({ title, reason }) {
        super();
        this.title = title;
        this.reason = reason;
    }

    get template() {
        return `
            <div class="p-5 text-center text-muted">
                <i class="bi bi-hourglass-split fs-2 d-block mb-2"></i>
                <div class="fw-semibold mb-1">${this.title}</div>
                <div class="small">${this.reason}</div>
            </div>
        `;
    }
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default class AnalyticsDashboardPage extends Page {
    constructor(options = {}) {
        super({
            title: 'Analytics',
            icon: 'bi-graph-up',
            pageName: 'analytics_dashboard',
            route: 'group/analytics',
            permissions: ['routes', 'view_routes', 'manage_routes', 'manage_group'],
            requiresGroup: true,
            ...options,
        });

        this._tabView = null;
    }

    async onInit() {
        this._tabView = new TabView({
            tabs: {
                'Completion Rate': new CompletionRateTab(),
                'Dwell Time': new DwellTimeTab(),
                'Operator Performance': new OperatorPerformanceTab(),
                'Missed Stops': new MissedStopsTab(),
                'Lock Access': new PlaceholderTab({ title: 'Lock Access', reason: 'No endpoint available yet.' }),
                'Battery Trends': new PlaceholderTab({ title: 'Battery Trends', reason: 'No endpoint available yet.' }),
                'Incidents': new PlaceholderTab({ title: 'Incidents', reason: 'Incidents API not yet built.' }),
                'Parts': new PlaceholderTab({ title: 'Parts Consumption', reason: 'Parts catalog API not yet built.' }),
            },
            activeTab: 'Completion Rate',
        });

        this.addChild(this._tabView, { containerId: 'tabs' });
        this.render();
    }

    get template() {
        return `
            <div class="d-flex align-items-center mb-3">
                <h5 class="mb-0">
                    <i class="bi bi-graph-up me-2"></i>Analytics
                </h5>
            </div>
            <div class="card">
                <div class="card-body p-0">
                    <div data-container="tabs"></div>
                </div>
            </div>
        `;
    }

    set template(v) { this._t = v; }
}
